<script setup lang="ts">
import type {
  ArtStyleClickEvent,
  ArtStyleGalleryItem,
  ArtStyleGridClass,
} from './artStyleTypes';

defineProps<{
  gridClass: ArtStyleGridClass;
  items: readonly ArtStyleGalleryItem[];
  locked: boolean;
}>();

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const emit = defineEmits<{
  select: [event: ArtStyleClickEvent, item: ArtStyleGalleryItem];
}>();
</script>

<template>
  <div
    class="ArtStyle__custom-grid"
    :class="[gridClass, { 'pointer-events-none': locked }]"
  >
    <div
      v-for="item in items"
      :key="item.uuid"
      class="ArtStyle__custom-item"
      data-testid="art-style-custom-item"
      :class="{ 'ArtStyle__custom-item--selected': item.selected }"
      @click="emit('select', null, item)"
    >
      <img
        :src="item.url"
        :alt="item.title"
        class="ArtStyle__custom-image"
      />
      <h3
        class="ArtStyle__custom-title"
        :class="{ 'ArtStyle__custom-title--selected': item.selected }"
      >
        {{ item.title }}
      </h3>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .ArtStyle__custom-grid {
    --ArtStyle--rows-visible: var(--art-style-grid-rows-visible, 2);
    --ArtStyle--gap: var(--art-style-grid-gap, 2vw);
    --ArtStyle--item-label-height: var(
      --art-style-grid-item-label-height,
      2.2rem
    );
    --ArtStyle--shadow-padding: var(
      --art-style-grid-shadow-padding,
      0.5rem
    );
    --ArtStyle--content-height: calc(
      var(--ArtStyle--grid-available) - var(--ArtStyle--shadow-padding) * 2
    );
    --ArtStyle--row-height: calc(
      (
          var(--ArtStyle--content-height) - var(--ArtStyle--gap) *
          (var(--ArtStyle--rows-visible) - 1)
        ) /
        var(--ArtStyle--rows-visible)
    );
    --ArtStyle--item-max-width: var(
      --art-style-grid-item-max-width,
      100000vw
    );
    --ArtStyle--item-image-height: min(
      calc(
        var(--ArtStyle--row-height) - var(--ArtStyle--item-label-height)
      ),
      calc(
        var(--ArtStyle--item-max-width) /
        var(--ArtStyle--preview-aspect-ratio)
      )
    );
    --ArtStyle--item-size: calc(
      var(--ArtStyle--item-image-height) *
      var(--ArtStyle--preview-aspect-ratio)
    );

    display: grid;
    justify-content: center;
    align-content: start;
    gap: var(--ArtStyle--gap);
    padding: var(--ArtStyle--shadow-padding);
    width: calc(
      var(--ArtStyle--item-size) * var(--art-style-grid-width-columns, 2) +
      var(--ArtStyle--gap) * var(--art-style-grid-width-gaps, 1) +
      var(--ArtStyle--shadow-padding) * 2
    );
    height: var(--ArtStyle--grid-available);
    max-height: var(--ArtStyle--grid-available);
    overflow-y: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }

    &--two {
      grid-template-columns: var(--ArtStyle--item-size);
      align-content: center;
    }

    &--three {
      grid-template-columns: repeat(
        var(--art-style-grid-column-count, 2),
        var(--ArtStyle--item-size)
      );

      .ArtStyle__custom-item:nth-child(3) {
        grid-column: var(--art-style-grid-special-column, 1 / -1);
        justify-self: center;
      }
    }

    &--four,
    &--even {
      grid-template-columns: repeat(
        var(--art-style-grid-column-count, 2),
        var(--ArtStyle--item-size)
      );
    }

    &--odd {
      grid-template-columns: repeat(
        var(--art-style-grid-column-count, 2),
        var(--ArtStyle--item-size)
      );

      .ArtStyle__custom-item:last-child {
        grid-column: var(--art-style-grid-special-column, 1 / -1);
        justify-self: center;
      }
    }
  }

  .ArtStyle__custom-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-self: var(--art-style-grid-item-justify-self, auto);
    width: var(--ArtStyle--item-size);
    cursor: pointer;

    // No :hover zoom — see ArtStyleCarousel.vue. Touch browsers keep :hover on
    // the last touched tile, so the effect stuck instead of following a cursor.
    &--selected {
      .ArtStyle__custom-image {
        box-shadow: 0 0 0 4px #6496FF;
      }

      .ArtStyle__custom-title {
        color: #6496FF;
      }
    }
  }

  .ArtStyle__custom-image {
    width: var(--ArtStyle--item-size);
    height: var(--ArtStyle--item-image-height);
    object-fit: cover;
    border-radius: 1rem;
    transition: box-shadow 0.2s ease;
  }

  .ArtStyle__custom-title {
    margin-top: var(--art-style-grid-title-margin-top, 0.6rem);
    font-size: var(--art-style-grid-title-font-size, 1rem);
    letter-spacing: 1px;
    text-transform: var(--Kiosk--font_capitalize, uppercase);
    text-align: center;
    color: var(--Kiosk--font_color);
  }
</style>
